/**
 * Template registry — maps `events.template_key` to its renderer metadata.
 *
 * Two halves of source-of-truth (see EVENT_AUTHORING_SPEC §6.2):
 *   - Backend: `Event::TEMPLATE_KEYS` in Ruby, the only validation gate
 *   - Frontend: this file, pairs keys with their UI surface
 *
 * Keys in this object MUST match `Event::TEMPLATE_KEYS` exactly. A drift
 * test should assert parity once the Rails side ships.
 */

import type { Layout, ShellName } from '../layout'
import { LE_VANILLA_DEFAULT_LAYOUT } from './le_vanilla/defaultLayout'
import { LE_VANILLA_MAP_DEFAULT_LAYOUT } from './le_vanilla_map/defaultLayout'

export interface TemplateRegistryEntry {
  /** Human-readable display name for composer / admin. */
  name: string
  /** Shell key the layout slots into. */
  shell: ShellName
  /** Default layout for linked events. Forking copies this. */
  defaultLayout: Layout
  /** Soft hints, not hard validation. See spec §5.4. */
  recommended_assets: ('route_gpx' | 'sponsors' | 'cover')[]
  /** Whether the template renders a public viewer page at all. */
  hasViewerPage: boolean
}

export const TEMPLATE_REGISTRY: Record<string, TemplateRegistryEntry> = {
  none: {
    name: 'Stream only',
    shell: 'le_vanilla', // unused — hasViewerPage is false
    defaultLayout: LE_VANILLA_DEFAULT_LAYOUT, // unused
    recommended_assets: [],
    hasViewerPage: false,
  },
  le_vanilla: {
    name: 'Le Vanilla',
    shell: 'le_vanilla',
    defaultLayout: LE_VANILLA_DEFAULT_LAYOUT,
    recommended_assets: [],
    hasViewerPage: true,
  },
  le_vanilla_map: {
    name: 'Le Vanilla + Map',
    shell: 'le_vanilla_map',
    defaultLayout: LE_VANILLA_MAP_DEFAULT_LAYOUT,
    recommended_assets: ['route_gpx'],
    hasViewerPage: true,
  },
}

/** Look up template metadata by key. Returns null when the key is unknown. */
export function lookupTemplate(key: string): TemplateRegistryEntry | null {
  return TEMPLATE_REGISTRY[key] ?? null
}
